// ./src/components/print/cardFields.ts
//
// WHICH ROWS A CARD PRINTS — declared once, read by BOTH the thing that MEASURES the card and the thing
// that RENDERS it.
//
// ⚠⚠ THAT IS THE ENTIRE POINT OF THE FILE. `cardSlotPlan` sizes the caption and details bands from a
// COUNT of switched-on rows; the card then draws the rows. If the two ever disagree — a row added to the
// renderer and not to the counter, or a default flipped in one place — the band is measured for four
// lines and drawn with five, and the fifth prints outside a slot that is itself perfectly correct. There
// is no error, no warning, and nothing looks wrong until paper comes out of a printer. So the list is
// the single source: `enabledRows()` produces what is counted, and the card maps over the SAME call.
//
// ⚠ NO IMPORTS BEYOND `cardSlots`, and `cardSlots` only imports `cardGeometry` — the whole chain is
// dependency-free so `check-print-sheets` can EXECUTE it rather than grep it.

import { textEmWidth } from "./cardGeometry";
import type { CardFieldSet } from "./cardSlots";

/** One line on a card, and the `settings` switch that decides whether it is printed. */
export interface CardRowSpec {
  /** How the card looks the value up. */
  key: string;
  /** The `print_design.settings` key this row is switched by. */
  settingKey: string;
  /** What happens when the design says nothing. ⚠ Must match what the card assumed before this file. */
  defaultOn: boolean;
  /** How the row is drawn. `strong` is the one line in the group a reader should land on first. */
  emphasis?: "strong" | "dim";
  /**
   * How many LINE BOXES this row may occupy. Default 1 — one line, ellipsised if it overruns.
   *
   * ⚠⚠ IT IS COUNTED, NOT JUST ALLOWED, and that is the whole reason it lives here rather than as a CSS
   * choice in the card. `cardSlotPlan` sizes the details band as `detailLines × pt × lineHeight`, so a
   * row that WRAPS without being counted draws two lines into a box measured for one — and the second
   * line prints outside a slot that is itself perfectly correct, with nothing visible on screen. The
   * band's height and the text drawn into it come from the same number or they do not agree at all.
   *
   * ⚠ It is a CEILING, not a target: a short value still paints one line and the rest of the box stays
   * empty, so every card on the sheet keeps the same grid whatever this particular subject's value is.
   * (Same rule as an empty row still painting its line box.)
   */
  maxLines?: number;
  /**
   * ⚠ PRINTED ONLY WHEN THE RUN SAYS IT HAS IT. A row whose value most events never set (a voucher's header, footer,
   * expiry, place to redeem) is DROPPED when the run's data is unknown, instead of the usual "keep every row" — and it is
   * the one kind of row filtered even in the caption band, which data otherwise never touches. Without it, a host who
   * never wrote a header would still give up a caption line to an empty one.
   */
  runOptional?: boolean;
}

/** Is `key` switched on? ⚠ Only a real boolean counts — a missing key takes the row's own default. */
export const settingOn = (
  settings: Record<string, any> | null | undefined,
  key: string,
  fallback: boolean,
): boolean => {
  const value = settings?.[key];

  return typeof value === "boolean" ? value : fallback;
};

/** The rows this design has switched on, in print order. */
export const enabledRows = (rows: CardRowSpec[], settings: Record<string, any> | null | undefined): CardRowSpec[] =>
  rows.filter((row) => settingOn(settings, row.settingKey, row.defaultOn));

/** How many line boxes one row occupies. ⚠ The card must draw a box exactly this tall. */
export const rowLines = (row: CardRowSpec): number => Math.max(1, Math.round(row.maxLines ?? 1));

/**
 * HOW MANY LINE BOXES THE SWITCHED-ON ROWS NEED — what `cardSlotPlan` sizes the band from.
 *
 * ⚠⚠ LINES, NOT ROWS. `detailLines` has always meant line boxes to the planner
 * (`detailLines × pt × lineHeight`), and it only coincided with a row count while every row was one
 * line. The moment one row may wrap, counting ROWS measures the band for less text than the card draws
 * — which is the "measured for four, drawn with five" failure this file exists to prevent.
 */
export const countedLines = (rows: CardRowSpec[], settings: Record<string, any> | null | undefined): number =>
  enabledRows(rows, settings).reduce((total, row) => total + rowLines(row), 0);

// ─────────────────────────────────────────────────────────────────────────────
// NAME BADGES
// ─────────────────────────────────────────────────────────────────────────────

/** The event title and date. ⚠ Captions, not headings — see `BadgeCard` for why. */
export const BADGE_CAPTION_ROWS: CardRowSpec[] = [
  { key: "eventTitle", settingKey: "showEventTitle", defaultOn: true },
  /**
   * ⚠ ON BY DEFAULT since 2026-09-24 (owner: "by default we want to show event date/time under event
   * name"). A voucher is redeemed on a DAY; a stall holder handed one with no date cannot tell this
   * year's from last year's, and the coupon's own validity row is about expiry, not about when the
   * event is.
   */
  { key: "eventWhen", settingKey: "showEventWhen", defaultOn: true },
];

export const BADGE_DETAIL_ROWS: CardRowSpec[] = [
  { key: "jobTitle", settingKey: "showJobTitle", defaultOn: true },
  { key: "company", settingKey: "showCompany", defaultOn: true, emphasis: "strong" },
  { key: "groupLabel", settingKey: "showGroup", defaultOn: true, emphasis: "dim" },
  { key: "seatLabel", settingKey: "showSeat", defaultOn: false, emphasis: "strong" },
  { key: "bibNumber", settingKey: "showBib", defaultOn: false, emphasis: "strong" },
  /**
   * ⚠⚠ EMAIL AND PHONE ARE TWO ROWS, NOT ONE JOINED BY A DOT. They were `a@b.com · +1 587 555 0199` on
   * a single line, and on a 4 × 3in badge — the LARGEST sheet stock in the catalog — that line still
   * ran out of room and ellipsised the phone number to "+158…". A contact detail that is cut off is not
   * a contact detail; it is ink. ⚠ Both rows are counted whether or not a given guest has both, so the
   * band is the same height on every card and nothing below it moves.
   */
  { key: "email", settingKey: "showContact", defaultOn: false, emphasis: "dim" },
  { key: "phone", settingKey: "showContact", defaultOn: false, emphasis: "dim" },
];

// ─────────────────────────────────────────────────────────────────────────────
// TICKETS
//
// ⚠ A TICKET'S IDENTITY SLOT IS THE HOLDER'S NAME, falling back to the ticket type when the ticket is
// not assigned to anybody — which is the normal case at a box office. ⚠ THE FALLBACK IS DATA, NOT
// GEOMETRY: it changes what is printed in the slot, never the slot, so an unassigned ticket and a named
// one still lay out identically on the same sheet.
// ─────────────────────────────────────────────────────────────────────────────

export const TICKET_CAPTION_ROWS: CardRowSpec[] = [
  { key: "eventTitle", settingKey: "showEventTitle", defaultOn: true },
  { key: "whenLabel", settingKey: "showEventWhen", defaultOn: true },
];

export const TICKET_DETAIL_ROWS: CardRowSpec[] = [
  { key: "ticketTypeName", settingKey: "showTicketType", defaultOn: true, emphasis: "strong" },
  { key: "venueLabel", settingKey: "showVenue", defaultOn: true, emphasis: "dim" },
  { key: "seatLabel", settingKey: "showSeat", defaultOn: true, emphasis: "strong" },
  { key: "priceLabel", settingKey: "showPrice", defaultOn: true, emphasis: "dim" },
  // ⚠ The code prints as TEXT as well as in the QR. A scanner that will not read a smudged card is the
  // normal case at a door, and typing the code is what keeps the queue moving.
  { key: "code", settingKey: "showCodeText", defaultOn: true, emphasis: "strong" },
];

// ─────────────────────────────────────────────────────────────────────────────
// COUPONS (ROADMAP A5 item 4)
//
// ⚠⚠ A COUPON'S IDENTITY SLOT IS THE COUPON TYPE — NOT the person (owner, 2026-09-24), and this is the
// one place a coupon's hierarchy is the INVERSE of a ticket's. A ticket's holder is checked against a
// door list, so the name IS the card. A coupon is handed over at a stall where nobody is checking who
// you are, and the thing that has to be readable across a counter is WHAT IT IS GOOD FOR. The first
// build mirrored the ticket and printed "Jim Tomson" at 30pt above a 6pt "Free Hot Drink + …", which
// inverts the only hierarchy the card has. The person is now an ordinary detail row.
// ⚠ It falls back to the issued-to name only when a type has no name at all, so the slot is never blank.
// ⚠ THE FALLBACK IS DATA, NOT GEOMETRY: it changes what is printed in the slot, never the slot.
//
// ⚠⚠ AND THE VALIDITY DATE IS A DETAIL ROW, NOT A CAPTION LINE — the one place this deliberately does
// NOT mirror the ticket. A ticket's caption carries `whenLabel`, which on a recurring event is the
// occurrence the ticket admits you to. A coupon's date is a RESTRICTION: a dated coupon is refused at
// the stall on any other day. Two reasons it cannot live in the caption:
//   - a caption is RUN-LEVEL by definition — `captionMaxEm` is sized once for the whole sheet because
//     "the event's name and date are the SAME on every card". Coupons on one sheet routinely carry
//     DIFFERENT dates, so a per-coupon caption line would be a subject's data sizing the grid, which is
//     the one thing this engine forbids;
//   - captions print dim and small. The line that decides whether a guest is turned away must not.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The size a card's detail rows are guaranteed at least (owner, 2026-09-24 — coupons, then badges and
 * tickets on the same ruling). ⚠ A FLOOR: a card with room derives larger and keeps it; shrink-to-fit
 * still goes below on stock that cannot hold 12pt. One constant because all three kinds were given the
 * same answer — split it per kind the day they diverge.
 */
export const CARD_DETAIL_PT = 12;

/** @deprecated alias — use `CARD_DETAIL_PT`. Kept so existing references keep reading. */
export const COUPON_DETAIL_PT = CARD_DETAIL_PT;

export const COUPON_CAPTION_ROWS: CardRowSpec[] = [
  /**
   * ⚠ THE HOST'S OWN HEADER (owner, 2026-10-25: "add a free text header and free text footer") — the event's, the same
   * on every card, so it is a CAPTION line: the band that is full-width, run-level, and never moves the QR. Bold rather
   * than dim, because the host wrote it to be read. Dropped for a run that has none (`runOptional`).
   */
  { key: "headerText", settingKey: "showHeader", defaultOn: true, emphasis: "strong", runOptional: true },
  { key: "eventTitle", settingKey: "showEventTitle", defaultOn: true },
  /**
   * ⚠ ON BY DEFAULT since 2026-09-24 (owner: "by default we want to show event date/time under event
   * name"). A voucher is redeemed on a DAY; a stall holder handed one with no date cannot tell this
   * year's from last year's, and the coupon's own validity row is about expiry, not about when the
   * event is.
   */
  { key: "eventWhen", settingKey: "showEventWhen", defaultOn: true },
];

/**
 * ⚠⚠ WHAT IT GIVES IS THE LEDE — a FULL-WIDTH row directly under the coupon's name, not a line in the
 * details band (owner, 2026-09-24, after reading a printed sheet: "the squares do not show fully what
 * coupons gives … move the text to the place right under the coupon name, and make qr-code smaller so
 * we have plenty of space").
 *
 * It used to sit in the details band BESIDE the QR, which on a 2½in square is about a third of the
 * card — so "Hot Drink, Popcorn / Croissant" printed as "Hot Drink, Popcorn /…" on all nine cards of a
 * sheet. ⚠ An ellipsised LIST is worse than a terse one: it tells the vendor there is more and not what
 * it is, so they hand over what they can see and the guest is short-changed by a rendering decision.
 *
 * ⚠ IT BELONGS WITH THE NAME, not with the code. "Free Hot Drink + Snack" is the coupon's name and
 * "Hot Drink, Popcorn / Croissant" is what that name means — the second line of a headline, which is
 * why widening the details band was the wrong fix and moving the row was the right one.
 *
 * ⚠ IT IS THE ONE ROW ALLOWED TO WRAP, because it is the one whose value is a LIST and therefore has no
 * useful length limit. Two lines is the ceiling rather than unlimited: the slot is a fixed share of the
 * identity band, and a five-item voucher must not push the name off a 2½in square.
 */
export const COUPON_LEDE_ROWS: CardRowSpec[] = [
  { key: "givesLabel", settingKey: "showGives", defaultOn: true, maxLines: 2 },
];

export const COUPON_DETAIL_ROWS: CardRowSpec[] = [
  /**
   * ⚠⚠ THE HUMAN NUMBER, AND IT IS THE FIRST DETAIL LINE ON PURPOSE (A4b item 1, owner 2026-09-24).
   * Its reader is not the vendor at the counter — it is whoever flips through the stack at the end of
   * the night reconciling "37 of 120" against a float, and they need to find it without reading the
   * card. First in the band is the only place on this fixed grid that offers that.
   * ⚠ NOT `strong`: emphasis marks the one line a reader should land on FIRST, and across a counter that
   * is still what the voucher gives. A serial number that shouts is a serial number in the way.
   * ⚠⚠ OFF BY DEFAULT — A MEASUREMENT, NOT A PREFERENCE. (`expiryLabel` was off for the same reason until the owner
   * asked, 2026-10-25, for vouchers to "print expiration if it exists": it is ON now, printed only on a run that HAS an
   * expiry, and a small stock pays for it in type size — which the sheet's pre-flight says. The coupon details
   * band is ALREADY over-subscribed: with every existing switch on it renders at 8.2–8.9pt against a
   * DECLARED 12. Measured on the two stocks the guard sweeps, with everything on:
   *
   *     rows added   2½in square (35702)   DYMO 30256
   *     none                   8.9             8.2
   *     one                    7.1             6.6   ← already under the 7pt floor
   *     both                   5.9             5.5
   *
   * So the card cannot afford EITHER of them as a default. Both are opt-in per design, in Print setup —
   * which is exactly what A4 item 5 built the switch surface for.
   * ⚠ Do not flip either to `true` without re-running that sweep. The 7pt floor is the owner's review
   * encoded, and the first version of this row defaulted to ON and broke it on every stock.
   * ⚠ An expiry the host has SET but is not printing is not left silent — the sheet's pre-flight says
   * so (`EventCouponsPrintPage`), because the server refuses an expired voucher whether the card
   * mentions it or not, and a guest turned away by a rule the paper never stated is the worse failure.
   */
  { key: "sequenceLabel", settingKey: "showSequence", defaultOn: false },
  /**
   * ⚠⚠ WHO IT WAS ISSUED TO IS A DETAIL ROW, NOT THE HEADLINE (owner, 2026-09-24) — the one place a
   * coupon's hierarchy is the INVERSE of a ticket's. A ticket's holder is checked against a door list,
   * so their name is the card; a coupon is handed over at a stall where nobody is checking who you are,
   * and what the vendor needs to read across a counter is WHAT THIS IS GOOD FOR. Printing "Jim Tomson"
   * at 30pt above a 6pt "Free Hot Drink + …" inverts the only hierarchy the card has.
   * ⚠ It is still printed: it is what stops a stack of vouchers being anybody's.
   */
  { key: "issuedToName", settingKey: "showIssuedTo", defaultOn: true },
  { key: "valueLabel", settingKey: "showValue", defaultOn: true, emphasis: "dim" },
  /**
   * ⚠⚠ THE DATE THIS COUPON IS VALID ON (A4 dated coupons), and it is STRONG on purpose. `CouponCore`
   * refuses a redemption on any other date, so a coupon whose card does not say which day it is for
   * sends the guest to queue at a stall that will turn them away. ⚠ It renders empty on an undated
   * coupon, which is most of them — and the row is still counted, so a sheet mixing dated and undated
   * coupons keeps one grid.
   */
  { key: "validLabel", settingKey: "showValidDate", defaultOn: true, emphasis: "strong" },
  /**
   * ⚠⚠ THE EXPIRY, AND IT IS NOT THE ROW ABOVE (A4b item 2). `validLabel` answers "which DAY of a
   * recurring event does this admit you on"; this answers "after which day is it worth nothing". A
   * coupon can carry both, they are refused by different rules, and sharing one row or one switch would
   * make a host turning off a date restriction silently turn off an expiry as well.
   * ⚠ STRONG, for the same reason `validLabel` is: the line that decides whether somebody is turned away
   * at a counter must not print dim and small.
   * ⚠ It renders empty on an event with no expiry, which is most of them — and `rowsInRun` then drops
   * the row for the whole run rather than reserving a blank line on every card.
   * ⚠ ENFORCED, not decorative: `CouponCore.redeem` refuses a coupon past it. An expiry that lives only
   * on the card sends the guest to queue at a stall that will turn them away.
   * ⚠ OFF by default for the reason measured on `sequenceLabel` above — the band has no room for a new
   * default row. The switch is only OFFERED on an event that actually has an expiry, and the sheet's
   * pre-flight says when one exists and is not being printed.
   */
  { key: "expiryLabel", settingKey: "showExpiry", defaultOn: true, emphasis: "strong", runOptional: true },
  /**
   * ⚠⚠ WHERE TO REDEEM IT (owner, 2026-10-25: "a reference to venue's entrance/service point record so we could set
   * where to redeem a voucher, then update the voucher printing so it prints … where to redeem"). The voucher type's
   * own place, else the event's — "Redeem at: Bar". Not strong: nothing refuses a voucher at another stall; it tells
   * the guest where to go. ON by default and dropped for a run that names no place (`runOptional`).
   */
  { key: "redeemAtLabel", settingKey: "showRedeemAt", defaultOn: true, runOptional: true },
  // ⚠ The code prints as TEXT as well as in the QR, for the same reason a ticket's does: a scanner that
  // will not read a thumbed card is the normal case at a stall, and typing the code keeps the queue moving.
  /**
   * ⚠ THE CODE PRINTS AS TEXT AS WELL AS IN THE QR — a scanner that will not read a thumbed card is the
   * normal case at a stall, and typing the code keeps the queue moving. LAST, and in the text column,
   * because that column has the card's full width; see `COUPON_CODE_ROW` for the measurement that ruled
   * out putting it under the QR.
   */
  { key: "code", settingKey: "showCodeText", defaultOn: true, emphasis: "strong" },
];

/**
 * ⚠⚠ THE CODE IS THE LAST DETAIL ROW — and it was tried UNDER THE QR first, at the QR's width, exactly as
 * asked. **It does not fit at any stock size we print, and the arithmetic is not close.** An 8-character
 * code is 5.78 ems wide: **24.5mm at 12pt**, 16.3mm at 8pt, and still **14.3mm at 7pt** — against a QR
 * 13.3mm across on the 12-up and 11.7mm on the 2½in square. Matching the widths means a code smaller than
 * anything a person reads at a stall, on the one row a scanner failure falls back to.
 * ⚠ Do not re-try it without widening the QR past what the card can give. Kept as a row so it sits at the
 * bottom of the text column, which has the card's full width.
 */
export const COUPON_CODE_ROW: CardRowSpec = { key: "code", settingKey: "showCodeText", defaultOn: true, emphasis: "strong" };

/**
 * ⚠ THE HOST'S OWN FOOTER — the small print ("Not exchangeable for cash"), across the bottom of the card at its full
 * width. A band of its own (`CardFieldSet.footerLines`), carved before the rest of the card is planned. Up to two lines
 * — the planner uses one when it fits — and dropped for a run that has none (`runOptional`).
 */
export const COUPON_FOOTER_ROWS: CardRowSpec[] = [
  { key: "footerText", settingKey: "showFooter", defaultOn: true, emphasis: "dim", maxLines: 2, runOptional: true },
];

// ─────────────────────────────────────────────────────────────────────────────
// QR TAGS (ROADMAP #172) — drawn by the COUPON's renderer (`CouponCard`, with these rows), so no fifth card component
// for B10 to port.
//
// ⚠ The identity slot is WHAT IT IS ("Wristband", "Locker key"), falling back to "QR tag" — a tag is handed to somebody,
// and the card must say what kind of thing it is. There is no lede: a tag gives nothing.
// ⚠ The NUMBER is on by default (unlike a voucher's): a stack of generated tags is handed out and counted by it, and the
// tag band has room — four rows against the voucher's five plus a two-line lede.
// ⚠ WHO HOLDS IT is usually empty when tags are printed (they are made first and handed out at the door) — `rowsInRun`
// drops the row for such a run rather than reserving a blank line on every card.
// ─────────────────────────────────────────────────────────────────────────────

export const QR_TAG_CAPTION_ROWS: CardRowSpec[] = [
  { key: "eventTitle", settingKey: "showEventTitle", defaultOn: true },
  { key: "eventWhen", settingKey: "showEventWhen", defaultOn: false },
];

export const QR_TAG_LEDE_ROWS: CardRowSpec[] = [];

export const QR_TAG_DETAIL_ROWS: CardRowSpec[] = [
  { key: "sequenceLabel", settingKey: "showSequence", defaultOn: true },
  { key: "issuedToName", settingKey: "showHolder", defaultOn: true },
  /** The session / date the tag is for — strong, like a voucher's date: it decides where it works. */
  { key: "validLabel", settingKey: "showValidDate", defaultOn: true, emphasis: "strong" },
  /** ⚠ The code as TEXT too: a crumpled wristband that will not scan is typed at the door. */
  { key: "code", settingKey: "showCodeText", defaultOn: true, emphasis: "strong" },
];

/**
 * The rows of `specs` that at least one subject in this RUN has data for. `undefined` means "not known",
 * which keeps every row — the safe default, because dropping a row nobody told us about would clip it.
 *
 * ⚠⚠ USED BY ALL THREE CARD KINDS. A badge whose run has no job titles, a ticket sheet with no seats and
 * no venue, a coupon run with no values — each reserved a blank line per card and shrank the rows that DO
 * print to make room for it. Found on coupons (declared 12pt printing at 9.8) and it was never
 * coupon-specific: badges and tickets carry more optional rows than coupons do.
 */
export const rowsInRun = (
  specs: CardRowSpec[],
  rowsWithData?: ReadonlySet<string> | null,
): CardRowSpec[] => {
  if (!rowsWithData) {
    // ⚠ Unknown keeps every row — except one that prints only when the run says it has it.
    return specs.filter((spec) => !spec.runOptional);
  }

  return specs.filter((spec) => rowsWithData.has(spec.key));
};

/**
 * The caption rows this run prints. ⚠ The caption is NEVER filtered by data (the event's name and date are the same on
 * every card) — except a `runOptional` row, which is kept only when the run has it. Shared by the planner's count and
 * the card's drawing, so the two cannot disagree.
 */
export const captionRowsInRun = (
  specs: CardRowSpec[],
  rowsWithData?: ReadonlySet<string> | null,
): CardRowSpec[] => specs.filter((spec) => !spec.runOptional || !!rowsWithData?.has(spec.key));

/** The widest caption line, in ems — 0 when there is nothing to measure. */
const captionEm = (lines: Array<string | null | undefined>): number =>
  lines.reduce((widest: number, line) => Math.max(widest, textEmWidth(String(line ?? "").trim())), 0);

/**
 * ⚠ THE FIELD SET IS COUNTS AND BOOLEANS, never a subject. It describes the RUN — what the host ticked —
 * and is therefore the same for every card on the sheet, which is what lets one plan serve all of them.
 */
export const badgeFieldSet = (
  settings: Record<string, any> | null | undefined,
  /** The caption's own text — the event's name and date, identical on every card of the run. */
  captionText: Array<string | null | undefined> = [],
  rowsWithData?: ReadonlySet<string> | null,
): CardFieldSet => ({
  captionLines: enabledRows(BADGE_CAPTION_ROWS, settings).length,
  captionMaxEm: captionEm(captionText),
  detailLines: enabledRows(rowsInRun(BADGE_DETAIL_ROWS, rowsWithData), settings).length,
  declaredDetailPt: CARD_DETAIL_PT,
  avatar: settingOn(settings, "showAvatar", false),
  qr: settingOn(settings, "showQr", false),
  qrSizeKey: String(settings?.qrSize ?? "md"),
});

export const ticketFieldSet = (
  settings: Record<string, any> | null | undefined,
  captionText: Array<string | null | undefined> = [],
  rowsWithData?: ReadonlySet<string> | null,
): CardFieldSet => ({
  captionLines: enabledRows(TICKET_CAPTION_ROWS, settings).length,
  captionMaxEm: captionEm(captionText),
  detailLines: enabledRows(rowsInRun(TICKET_DETAIL_ROWS, rowsWithData), settings).length,
  declaredDetailPt: CARD_DETAIL_PT,
  avatar: false,
  qr: settingOn(settings, "showQr", true),
  qrSizeKey: String(settings?.qrSize ?? "md"),
});

/**
 * ⚠ A COUPON NEVER CARRIES A PORTRAIT. A voucher is handed over and taken away; the face on it is
 * nobody's business at a stall, and the square would come out of the room the "what it gives" line
 * needs. Stated as a constant `false` rather than a setting, so it cannot be switched on by a stray key.
 * ⚠ THE QR DEFAULTS ON AND IS NOT REALLY OPTIONAL: the redemption console scans it, so a coupon printed
 * without one can only be redeemed by typing — which is why every seeded coupon design has it on.
 */
/**
 * ⚠⚠ ROWS THAT ARE EMPTY FOR THE WHOLE RUN ARE DROPPED, and this is squarely inside the grid rule rather
 * than an exception to it. **A FIELD SWITCH IS RUN-LEVEL and may reshape the grid; a SUBJECT's data may
 * never move anything.** "No coupon on this sheet has a value" is a fact about the RUN — every card
 * re-plans together and they all still agree.
 *
 * ⚠ IT IS NOT COSMETIC. The owner's design has `showValue` and `showValidDate` on while every coupon in
 * the run has neither, so the band reserved two blank lines — and the fitter shrank the rows that DO
 * print from the declared 12pt down to **9.8pt** to make room for them. Legible text was being traded
 * for reserved emptiness.
 * ⚠ A row empty for SOME subjects still holds its box; that is the rule doing its job, and it is why the
 * page must pass "has data ANYWHERE in the run", never "has data on this card".
 */
export const couponFieldSet = (
  settings: Record<string, any> | null | undefined,
  captionText: Array<string | null | undefined> = [],
  rowsWithData?: ReadonlySet<string> | null,
  /** The footer's own text — the event's, the same on every card; sizes the footer like `captionText` sizes the caption. */
  footerText?: string | null,
): CardFieldSet => ({
  captionLines: enabledRows(captionRowsInRun(COUPON_CAPTION_ROWS, rowsWithData), settings).length,
  captionMaxEm: captionEm(captionText),
  footerLines: countedLines(rowsInRun(COUPON_FOOTER_ROWS, rowsWithData), settings),
  footerMaxEm: captionEm([footerText]),
  // ⚠ LINES, not rows — the "what it gives" row may take two, and the band has to be measured for what
  // the card will actually draw. See `countedLines`.
  /**
   * ⚠ THE LEDE'S LINES ARE COUNTED HERE TOO, because the planner decides per ARRANGEMENT whether to
   * carve a lede at all — and where it does not, that row is drawn in the details band. Counting it in
   * both places sizes the band for the worst case; the cost is a slightly smaller detail type on the
   * cards that do carve one, which is the safe direction (a band planned for more lines than it draws
   * never clips).
   */
  detailLines:
    countedLines(rowsInRun(COUPON_DETAIL_ROWS, rowsWithData), settings) +
    countedLines(rowsInRun(COUPON_LEDE_ROWS, rowsWithData), settings),
  ledeLines: countedLines(rowsInRun(COUPON_LEDE_ROWS, rowsWithData), settings),
  alignTextLeft: true,
  // ⚠ DECLARED, not derived (owner, 2026-09-24). A coupon's rows are read across a counter by somebody
  // deciding what to hand over; 12pt is the size that was asked for, and the fitter still shrinks it on
  // stock too small to hold it — which is every label, and correctly so.
  declaredDetailPt: CARD_DETAIL_PT,
  avatar: false,
  qr: settingOn(settings, "showQr", true),
  qrSizeKey: String(settings?.qrSize ?? "md"),
});

/** #172 — a QR tag's field set: the coupon's shape (same planner, same renderer) over the tag's rows. No portrait. */
export const qrTagFieldSet = (
  settings: Record<string, any> | null | undefined,
  captionText: Array<string | null | undefined> = [],
  rowsWithData?: ReadonlySet<string> | null,
): CardFieldSet => ({
  captionLines: enabledRows(QR_TAG_CAPTION_ROWS, settings).length,
  captionMaxEm: captionEm(captionText),
  detailLines: countedLines(rowsInRun(QR_TAG_DETAIL_ROWS, rowsWithData), settings),
  ledeLines: 0,
  alignTextLeft: true,
  declaredDetailPt: CARD_DETAIL_PT,
  avatar: false,
  qr: settingOn(settings, "showQr", true),
  qrSizeKey: String(settings?.qrSize ?? "md"),
});

// ─────────────────────────────────────────────────────────────────────────────
// WHICH SWITCHES A HOST IS OFFERED — DERIVED FROM THE SPECS ABOVE, NEVER RE-LISTED
//
// ⚠⚠ THIS EXISTS BECAUSE THE BADGE SHEET USED TO HAND-WRITE ITS OWN LIST (ROADMAP A4 item 5). The specs
// above already declare every `settingKey` and its `defaultOn`; a second list beside them is a second
// implementation, and nothing made the two agree. It is the same divergence this file's header is about
// — a row known to one half of the engine and not the other — only here the symptom is a switch that
// silently stops existing, or one offered for a row no card draws.
//
// ⚠ A SWITCH IS NOT A ROW. `showContact` drives BOTH the email row and the phone row on a badge, and
// `showEventWhen` appears on all three kinds. Collapse by `settingKey` and keep every row it drives, or
// a host is shown "Email and phone" twice and turning one off does nothing.
// ─────────────────────────────────────────────────────────────────────────────

/** One switch a host may flip, and every row it turns on or off. */
export interface CardFieldSwitch {
  /** The `print_design.settings` key. */
  settingKey: string;
  /**
   * The `CardRowSpec.key`s this switch drives. **Empty means it is not a row at all** — the QR and the
   * portrait are parts of the card's geometry rather than lines of text, so no run-level row data can
   * say whether to offer them.
   */
  rowKeys: string[];
  /** What the card does when the design says nothing — taken from the rows, never guessed. */
  defaultOn: boolean;
}

/**
 * The switches for one kind, in PRINT ORDER, one per distinct `settingKey`.
 *
 * ⚠ `extra` is for switches that are not rows (the QR, the portrait). Its `defaultOn` must match what
 * the kind's `*FieldSet` passes to `settingOn` — `check-print-sheets` executes both and compares, because
 * a drawer that shows "QR code" unticked while every card prints one is worse than no switch at all.
 */
export const fieldSwitches = (groups: CardRowSpec[][], extra: CardFieldSwitch[] = []): CardFieldSwitch[] => {
  const byKey = new Map<string, CardFieldSwitch>();

  for (const group of groups) {
    for (const row of group) {
      const found = byKey.get(row.settingKey);

      if (found) {
        if (!found.rowKeys.includes(row.key)) {
          found.rowKeys.push(row.key);
        }

        continue;
      }

      byKey.set(row.settingKey, { settingKey: row.settingKey, rowKeys: [row.key], defaultOn: row.defaultOn });
    }
  }

  for (const one of extra) {
    if (!byKey.has(one.settingKey)) {
      byKey.set(one.settingKey, { ...one, rowKeys: [...one.rowKeys] });
    }
  }

  return [...byKey.values()];
};

/**
 * The `settingKey`s whose rows DISAGREE about `defaultOn`. ⚠ Must be empty: one switch cannot be both on
 * and off for the rows it drives, so a disagreement means half the rows are lying about their own state.
 * Asserted by `check-print-sheets` rather than resolved silently here.
 */
export const conflictingDefaults = (groups: CardRowSpec[][]): string[] => {
  const seen = new Map<string, boolean>();
  const bad = new Set<string>();

  for (const group of groups) {
    for (const row of group) {
      if (seen.has(row.settingKey) && seen.get(row.settingKey) !== row.defaultOn) {
        bad.add(row.settingKey);
      }

      seen.set(row.settingKey, row.defaultOn);
    }
  }

  return [...bad];
};

export const BADGE_FIELD_SWITCHES: CardFieldSwitch[] = fieldSwitches(
  [BADGE_CAPTION_ROWS, BADGE_DETAIL_ROWS],
  [
    // ⚠ Defaults mirror `badgeFieldSet` exactly — see the note on `fieldSwitches`.
    { settingKey: "showAvatar", rowKeys: [], defaultOn: false },
    { settingKey: "showQr", rowKeys: [], defaultOn: false },
  ],
);

export const TICKET_FIELD_SWITCHES: CardFieldSwitch[] = fieldSwitches(
  [TICKET_CAPTION_ROWS, TICKET_DETAIL_ROWS],
  [{ settingKey: "showQr", rowKeys: [], defaultOn: true }],
);

/**
 * ⚠ NO PORTRAIT SWITCH. `couponFieldSet` states `avatar: false` as a constant on purpose — a voucher is
 * handed over at a stall and the face on it is nobody's business — so offering the toggle would be
 * offering a control that cannot do anything.
 */
export const COUPON_FIELD_SWITCHES: CardFieldSwitch[] = fieldSwitches(
  [COUPON_CAPTION_ROWS, COUPON_LEDE_ROWS, COUPON_DETAIL_ROWS, COUPON_FOOTER_ROWS],
  [{ settingKey: "showQr", rowKeys: [], defaultOn: true }],
);

/** #172 — a tag's switches, derived from its rows like every other kind's. No portrait. */
export const QR_TAG_FIELD_SWITCHES: CardFieldSwitch[] = fieldSwitches(
  [QR_TAG_CAPTION_ROWS, QR_TAG_LEDE_ROWS, QR_TAG_DETAIL_ROWS],
  [{ settingKey: "showQr", rowKeys: [], defaultOn: true }],
);

/**
 * ⚠⚠ THE SWITCHES OF WHICH AT LEAST ONE MUST STAY ON, OR THE CARD CANNOT BE REDEEMED AT ALL.
 *
 * A ticket and a coupon carry their identity twice on purpose — in the QR a scanner reads, and as TEXT a
 * person types when the scanner will not read a thumbed card. Both of those are switches, so a host can
 * turn off each one separately and be left with a card that prints perfectly and is worth nothing: the
 * stall cannot scan it and cannot look it up either. **Nothing downstream can recover from it** — the
 * paper is already printed and handed out, and the redemption console has no other handle on the row.
 *
 * ⚠ It is a rule about the LAST one, not about either key: turning the QR off while the code prints is a
 * legitimate choice (and the reverse is the normal one on tiny stock). Only the second one is refused.
 * ⚠ A badge has no such pair — its identity is a name a person reads — so the list is deliberately empty
 * rather than absent, to say that the question was asked.
 */
export const TICKET_IDENTITY_KEYS: string[] = ["showQr", "showCodeText"];
export const COUPON_IDENTITY_KEYS: string[] = ["showQr", "showCodeText"];
export const BADGE_IDENTITY_KEYS: string[] = [];
/** #172 — a tag is scanned at the door or typed: the QR or the code must print. */
export const QR_TAG_IDENTITY_KEYS: string[] = ["showQr", "showCodeText"];
